import type { IdsReport } from '@manudev/model';
import { create } from 'zustand';
import { useHeroStore } from '@/store/store';
import { readSet, SHIPPED, type RuleSet } from './sets';

export type RulesStatus = 'idle' | 'checking' | 'ready' | 'error';

interface RulesState {
  sets: RuleSet[];
  active: string;
  status: RulesStatus;
  error: string | null;
  report: IdsReport | null;
  checkedAt: string | null;
  // the rule set and store version the report, or the check in flight, belongs to
  ranFor: string | null;
  // a GlobalId the panel wants framed and selected; the station picks it up next frame
  focus: string | null;
  choose: (id: string) => void;
  drop: (name: string, xml: string) => void;
  run: () => Promise<void>;
  requestFocus: (globalId: string | null) => void;
}

export const runKey = (active: string, version: number) => `${active}@${version}`;

export const useRulesStore = create<RulesState>((set, get) => ({
  sets: SHIPPED,
  active: SHIPPED[0]!.id,
  status: 'idle',
  error: null,
  report: null,
  checkedAt: null,
  ranFor: null,
  focus: null,
  choose: (active) =>
    set((s) => (s.active === active ? s : { active, report: null, status: 'idle', error: null })),
  drop: (name, xml) => {
    const id = `drop-${name}`;
    set((s) => ({
      sets: [...s.sets.filter((x) => x.id !== id), { id, label: name, xml }],
      active: id,
      report: null,
      status: 'idle',
      error: null,
      // the same file dropped twice runs twice
      ranFor: null,
    }));
  },
  run: async () => {
    const { sets, active } = get();
    const store = useHeroStore.getState();
    const key = runKey(active, store.version);
    set({ ranFor: key, status: 'checking', error: null });
    try {
      const ruleSet = sets.find((s) => s.id === active);
      if (!ruleSet) throw new Error(`unknown rule set ${active}`);
      const report = await store.checkIds(await readSet(ruleSet));
      if (get().ranFor === key)
        set({ report, status: 'ready', checkedAt: new Date().toISOString() });
    } catch (err) {
      const error = String((err as Error)?.message ?? err);
      if (get().ranFor === key) set({ report: null, status: 'error', error });
    }
  },
  requestFocus: (focus) => set({ focus }),
}));
